import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { BookOpen, ArrowRight, Clock, Tag } from 'lucide-react';
import useSEO from '../utils/useSEO';

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.55, delay: i * 0.08, ease: 'easeOut' },
  }),
};

export const BLOG_POSTS = [
  // Finance blogs
  {
    slug: 'best-health-insurance-plans-for-students',
    title: 'Best Health Insurance Plans for Students in India 2026',
    category: 'Finance',
    tag: 'Insurance',
    date: 'June 2026',
    readTime: '5 min read',
    excerpt:
      'As a student, health emergencies can be financially draining. Here are the top health insurance plans in India specifically suited for students, with low premiums and wide coverage.',
    image: null,
    color: 'from-blue-500 to-indigo-600',
  },
  {
    slug: 'how-to-choose-life-insurance-india',
    title: 'How to Choose the Right Life Insurance in India',
    category: 'Finance',
    tag: 'Insurance',
    date: 'June 2026',
    readTime: '6 min read',
    excerpt:
      'Choosing life insurance is one of the most important financial decisions. This guide explains the key factors — term vs whole life, coverage amount, riders, and comparing plans.',
    image: null,
    color: 'from-green-500 to-teal-600',
  },
  {
    slug: 'investment-plans-for-young-professionals',
    title: 'Best Investment Plans for Young Professionals in 2026',
    category: 'Finance',
    tag: 'Investment',
    date: 'May 2026',
    readTime: '7 min read',
    excerpt:
      'Starting your investment journey early is the smartest financial move. Explore SIPs, mutual funds, PPF, and ELSS plans that young professionals in India should consider.',
    image: null,
    color: 'from-orange-500 to-red-500',
  },
  // Student earning blogs
  {
    slug: 'how-students-can-earn-5000-per-month',
    title: 'How Students Can Earn ₹5000 Per Month in India',
    category: 'Student Earning',
    tag: 'Earn Money',
    date: 'June 2026',
    readTime: '4 min read',
    excerpt:
      'Earning while studying is easier than you think. Discover practical ways Indian college students can earn ₹5000 or more per month through online and offline opportunities.',
    image: null,
    color: 'from-purple-500 to-pink-500',
  },
  {
    slug: 'best-side-hustles-for-college-students',
    title: 'Best Side Hustles for College Students in India 2026',
    category: 'Student Earning',
    tag: 'Side Hustle',
    date: 'May 2026',
    readTime: '5 min read',
    excerpt:
      'From freelancing to content writing to AGO Innovations\' Learn & Earn programs — here are the best side hustles for Indian college students that actually pay.',
    image: null,
    color: 'from-yellow-500 to-orange-500',
  },
  {
    slug: 'how-to-complete-assignments-faster',
    title: 'How to Complete Assignments Faster — 10 Proven Tips',
    category: 'Student Tips',
    tag: 'Assignments',
    date: 'May 2026',
    readTime: '4 min read',
    excerpt:
      'Struggling to meet assignment deadlines? These 10 proven strategies will help you write assignments faster without compromising quality — used by top students.',
    image: null,
    color: 'from-cyan-500 to-blue-600',
  },
  // Project blogs
  {
    slug: 'top-10-final-year-project-ideas-2026',
    title: 'Top 10 Final Year Project Ideas for Engineering Students 2026',
    category: 'Projects',
    tag: 'Final Year',
    date: 'June 2026',
    readTime: '6 min read',
    excerpt:
      'Stuck on your final year project topic? Here are 10 innovative and implementable engineering project ideas for 2026 — covering AI, IoT, web development, and more.',
    image: null,
    color: 'from-indigo-500 to-purple-600',
  },
  {
    slug: 'engineering-project-ideas-2026',
    title: 'Engineering Project Ideas 2026 — Branch-Wise Complete List',
    category: 'Projects',
    tag: 'Engineering',
    date: 'May 2026',
    readTime: '8 min read',
    excerpt:
      'A comprehensive branch-wise list of engineering project ideas for Computer, Mechanical, Civil, Electronics, and Electrical students in 2026.',
    image: null,
    color: 'from-blue-600 to-cyan-600',
  },
  {
    slug: 'science-exhibition-models-for-schools',
    title: 'Best Science Exhibition Models for School Students 2026',
    category: 'Projects',
    tag: 'School',
    date: 'April 2026',
    readTime: '5 min read',
    excerpt:
      'Planning for your school science exhibition? Here are the best, easiest, and most impressive science models for Class 6–12 students that guarantee top marks.',
    image: null,
    color: 'from-emerald-500 to-green-600',
  },
  {
    slug: 'website-project-ideas-for-students',
    title: 'Website Project Ideas for Students — Beginner to Advanced',
    category: 'Projects',
    tag: 'Web Dev',
    date: 'April 2026',
    readTime: '5 min read',
    excerpt:
      'Looking to build a web project for college submission or to add to your portfolio? From simple HTML projects to full-stack apps — here are the best website project ideas for students.',
    image: null,
    color: 'from-rose-500 to-pink-600',
  },
  // Month 2 — New Posts
  {
    slug: 'what-is-term-insurance-india',
    title: 'What is Term Insurance? Complete Guide for Indians in 2026',
    category: 'Finance',
    tag: 'Insurance',
    date: 'June 2026',
    readTime: '6 min read',
    excerpt:
      'Term insurance is the purest form of life insurance — maximum cover at minimum cost. This guide explains how it works, who needs it, and what to look for when buying a term plan in India.',
    image: null,
    color: 'from-blue-600 to-blue-800',
  },
  {
    slug: 'how-to-start-sip-investment-india',
    title: 'How to Start a SIP Investment in India — Step by Step Guide',
    category: 'Finance',
    tag: 'Investment',
    date: 'June 2026',
    readTime: '5 min read',
    excerpt:
      'Starting a SIP (Systematic Investment Plan) is the simplest way to build wealth in India. This step-by-step guide shows you exactly how to start your first SIP with as little as ₹500/month.',
    image: null,
    color: 'from-emerald-500 to-green-700',
  },
  {
    slug: 'mutual-fund-vs-fd-which-is-better',
    title: 'Mutual Fund vs Fixed Deposit — Which is Better in 2026?',
    category: 'Finance',
    tag: 'Investment',
    date: 'June 2026',
    readTime: '5 min read',
    excerpt:
      'Should you invest in a mutual fund SIP or a bank Fixed Deposit? This comparison breaks down returns, tax efficiency, liquidity, and risk to help you make the right choice.',
    image: null,
    color: 'from-teal-500 to-cyan-700',
  },
  {
    slug: 'car-insurance-guide-india-2026',
    title: 'Complete Car Insurance Guide for India 2026 — Everything You Need to Know',
    category: 'Finance',
    tag: 'Insurance',
    date: 'June 2026',
    readTime: '6 min read',
    excerpt:
      'Confused about car insurance? This complete guide covers third-party vs comprehensive plans, No Claim Bonus, zero depreciation add-ons, and how to choose the best motor insurance in India.',
    image: null,
    color: 'from-orange-500 to-amber-700',
  },
  {
    slug: 'online-earning-for-students-without-investment',
    title: 'Online Earning for Students Without Investment in India',
    category: 'Student Earning',
    tag: 'Earn Money',
    date: 'June 2026',
    readTime: '5 min read',
    excerpt:
      'Want to earn money online as a student without investing any money? Here are 8 proven ways Indian college students can start earning online today — no upfront investment required.',
    image: null,
    color: 'from-violet-500 to-purple-700',
  },
  {
    slug: 'student-freelancing-guide-india',
    title: 'Student Freelancing in India — How to Start and Earn in 2026',
    category: 'Student Earning',
    tag: 'Freelancing',
    date: 'May 2026',
    readTime: '6 min read',
    excerpt:
      'Freelancing is one of the best ways for Indian students to earn money with flexible hours. This guide covers platforms, skills, pricing, and how to land your first freelance gig.',
    image: null,
    color: 'from-fuchsia-500 to-pink-700',
  },
  {
    slug: 'journal-writing-tips-for-engineering-students',
    title: 'Journal Writing Tips for Engineering Students — Format & Examples',
    category: 'Student Tips',
    tag: 'Journals',
    date: 'June 2026',
    readTime: '4 min read',
    excerpt:
      'Struggling with engineering journal writing? This guide covers the exact format, structure, and tips for writing lab reports, observation journals, and academic journals that score well.',
    image: null,
    color: 'from-cyan-500 to-teal-700',
  },
  {
    slug: 'college-project-report-writing-guide',
    title: 'How to Write a College Project Report — Format & Tips 2026',
    category: 'Student Tips',
    tag: 'Projects',
    date: 'June 2026',
    readTime: '5 min read',
    excerpt:
      'Writing a college project report for the first time? This guide covers the complete structure, formatting standards, references, and common mistakes to avoid in your project documentation.',
    image: null,
    color: 'from-indigo-500 to-blue-700',
  },
  {
    slug: 'assignment-writing-tips-engineering-students',
    title: 'Assignment Writing Tips for Engineering Students — Score Better',
    category: 'Student Tips',
    tag: 'Assignments',
    date: 'May 2026',
    readTime: '4 min read',
    excerpt:
      'Engineering assignments are different from school essays. These 8 tips specifically for engineering students will help you write better, faster, and score higher marks on every submission.',
    image: null,
    color: 'from-sky-500 to-blue-700',
  },
  {
    slug: 'science-project-ideas-class-10-cbse',
    title: 'Science Project Ideas for Class 10 CBSE — Best for Exhibitions 2026',
    category: 'Projects',
    tag: 'School',
    date: 'June 2026',
    readTime: '5 min read',
    excerpt:
      'Looking for impressive science project ideas for Class 10 CBSE? Here are 15 working model ideas that are easy to make, visually impactful, and guaranteed to impress at science exhibitions.',
    image: null,
    color: 'from-lime-500 to-green-700',
  },
];

const CATEGORIES = ['All', 'Finance', 'Student Earning', 'Student Tips', 'Projects'];

const BlogPage = () => {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState('All');

  useSEO({
    title: 'Blog | Assignment Tips, Student Earning & Finance Insights | AGO Innovations',
    description:
      'Read expert articles on assignment writing tips, student earning ideas, insurance guidance, investment planning, and project ideas — all on the AGO Innovations blog.',
    keywords:
      'student blog India, assignment tips, earn money as student, insurance guide India, investment tips, engineering project ideas, school science models',
    canonical: 'https://agoinnovations.in/blog',
  });

  const filtered =
    activeCategory === 'All'
      ? BLOG_POSTS
      : BLOG_POSTS.filter((p) => p.category === activeCategory);

  return (
    <div className="min-h-screen bg-white">
      {/* HERO */}
      <section className="bg-gradient-to-br from-gray-900 via-blue-900 to-indigo-900 pt-28 pb-20 px-4 text-white text-center relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-yellow-400 via-orange-400 to-yellow-400" />
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="relative z-10 max-w-3xl mx-auto"
        >
          <span className="inline-block bg-white/10 border border-white/20 text-blue-200 text-sm font-semibold px-5 py-1.5 rounded-full mb-5">
            📝 AGO Innovations Blog
          </span>
          <h1 className="text-4xl md:text-5xl font-black mb-4 leading-tight">
            Insights for Students
            <span className="block text-yellow-400">& Professionals</span>
          </h1>
          <p className="text-blue-100 text-lg max-w-xl mx-auto">
            Assignment tips, student earning ideas, insurance guides, project inspiration — written for you.
          </p>
        </motion.div>
      </section>

      {/* CATEGORY FILTER */}
      <section className="py-8 px-4 bg-white border-b border-gray-100 sticky top-0 z-20 shadow-sm">
        <div className="max-w-5xl mx-auto flex flex-wrap justify-center gap-3">
          {CATEGORIES.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-5 py-2 rounded-full font-semibold text-sm transition-all duration-200 ${
                activeCategory === cat
                  ? 'bg-blue-700 text-white shadow-md'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      </section>

      {/* BLOG GRID */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((post, i) => (
              <motion.article
                key={post.slug}
                custom={i}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                whileHover={{ y: -5 }}
                onClick={() => navigate(`/blog/${post.slug}`)}
                className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-lg transition-all duration-300 cursor-pointer overflow-hidden"
              >
                {/* Card header */}
                <div className={`h-28 bg-gradient-to-br ${post.color} flex items-center justify-center`}>
                  <BookOpen className="w-12 h-12 text-white/70" />
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="bg-blue-50 text-blue-700 text-xs font-semibold px-3 py-1 rounded-full flex items-center gap-1">
                      <Tag className="w-3 h-3" /> {post.tag}
                    </span>
                    <span className="text-gray-400 text-xs flex items-center gap-1">
                      <Clock className="w-3 h-3" /> {post.readTime}
                    </span>
                  </div>
                  <h2 className="font-black text-gray-900 text-sm leading-snug mb-3 line-clamp-2">
                    {post.title}
                  </h2>
                  <p className="text-gray-500 text-xs leading-relaxed mb-4 line-clamp-3">
                    {post.excerpt}
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400 text-xs">{post.date}</span>
                    <span className="text-blue-600 font-semibold text-xs flex items-center gap-1 hover:gap-2 transition-all">
                      Read More <ArrowRight className="w-3 h-3" />
                    </span>
                  </div>
                </div>
              </motion.article>
            ))}
          </div>

          {filtered.length === 0 && (
            <div className="text-center py-20 text-gray-400 text-lg">
              No posts in this category yet. Check back soon!
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4 bg-gradient-to-br from-blue-800 to-indigo-900 text-white text-center">
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="max-w-2xl mx-auto"
        >
          <h2 className="text-2xl md:text-3xl font-black mb-4">Need Assignment Help or Finance Advice?</h2>
          <p className="text-blue-200 mb-8">Explore our services — assignment writing, workshops, insurance, and investment planning.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate('/assignment-writing-service')}
              className="bg-yellow-400 hover:bg-yellow-300 text-blue-900 font-bold px-8 py-3 rounded-full transition-colors"
            >
              Assignment Help
            </button>
            <button
              onClick={() => navigate('/finance')}
              className="bg-white/10 hover:bg-white/20 text-white font-bold px-8 py-3 rounded-full border border-white/30 transition-colors"
            >
              Finance Services
            </button>
          </div>
        </motion.div>
      </section>
    </div>
  );
};

export default BlogPage;
